import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isBefore } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { Project } from 'src/app/interfaces/api/project';
import { defaultTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-merge-trackers-modal',
  imports: [CommonModule,
    FormsModule, TranslateModule,
    MatDialogModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatListModule,
    MatChipsModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatToolbarModule,
    ButtonComponent,
    MatCardModule
  ],
  standalone: true,
  templateUrl: './merge-trackers-modal.component.html',
  styleUrls: ['./merge-trackers-modal.component.scss']
})

export class MergeTrackersModal implements OnInit, OnDestroy {
  trackers: any[] = [];
  user!: User;
  trackerId: string = "";
  activeProject: any;
  mergedProjects: Project[] = [];
  configs: any[] = [];
  stats: string = "";
  mergedTracker: Tracker = defaultTracker;
  rightTracker: Tracker = defaultTracker;
  leftTracker: Partial<Tracker> = defaultTracker;
  mergeLeftToRight = true;
  leftProjects: Map<string, any> = new Map();
  rightProjects: Map<string, any> = new Map();
  leftUUID: string = "";
  rightUUID: string = "";
  isDark = false;
  activity: any;
  loadingProjectList = false;
  invalidUserRole = false;
  destroy$ = new Subject<void>();
  allProjectsFrozen: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public trackerService: TrackerService,
    public projectService: ProjectService,
    private pop: Pop,
    public dialogRef: MatDialogRef<MergeTrackersModal>,
    private translate: TranslateService) {
    if (data.trackers?.length == 2) {
      this.trackers = data.trackers.map((t: Tracker) => t);
      this.trackers.forEach(t => t.id = t._id);
      this.trackers.sort((a, b) => a.lastUpdate == b.lastUpdate ? 0 : isBefore(a.lastUpdate, b.lastUpdate) ? -1 : 1);
      this.leftTracker = this.trackers[0];
      if (!this.leftTracker.projects?.length)
        this.leftTracker.projects = [];
      this.leftUUID = this.leftTracker.uuid as string;
      this.rightTracker = this.trackers[this.trackers.length - 1];
      this.rightUUID = this.rightTracker.uuid as string;
      if (!this.rightTracker.projects?.length)
        this.rightTracker.projects = [];
      this.assignOrderToProjects();
      this.ensureActiveIsLast();
    }

    if (data.user) {
      this.user = data.user
      this.isDark = this.user.theme == "dark";

      if (this.user.role?.name != "dev" && this.user.role?.name != "admin") {
        this.pop.error(this.translate.instant("user.access_control"));
        this.invalidUserRole = true;
        this.dialogRef.close();
      }
    }
  }

  ngOnInit() {
    if (this.invalidUserRole) return;
    if (this.allProjectsFrozen) return;

    let databases = (this.mergedTracker.projects as Project[]).filter(p => !p.frozen).map(p => p.database);

    this.loadingProjectList = true;
    this.trackerService.getLastUpdatesByDb({ databases: databases, trackerName: this.leftTracker.name, uuids: this.trackers.map(t => t.uuid) }).pipe(takeUntil(this.destroy$)).subscribe((result: { tracker: Tracker, database: string }[]) => {
      this.trackers.forEach(tracker => {
        // Assign lastUpdate (if tracker exists) field to projects
        tracker.projects.forEach((p: Project) => {
          let trackerInfo = result.find(data => data.database == p.database);
          if (trackerInfo && trackerInfo.tracker)
            p.lastUpdate = trackerInfo.tracker.lastUpdate as string;
          else if (trackerInfo && !trackerInfo.tracker && tracker.project.database == trackerInfo.database) {
            p.lastUpdate = tracker.lastUpdate;
            trackerInfo.tracker = tracker;
          } else if (p.frozen) {
            p.lastUpdate = tracker.lastUpdate;
            result.push({ database: p.database, tracker });
          }
        });
        // Sort tracker.projects (active project last, if tracker doesn't exist then by order index, otherwise by project.lastUpdate)
        tracker.projects.sort((p1: Project, p2: Project) => {
          let trackerInfo1 = result.find(data => data.database == p1.database);
          let trackerInfo2 = result.find(data => data.database == p2.database);
          if (!trackerInfo1 || !trackerInfo2 || !trackerInfo1.tracker || !trackerInfo2.tracker)
            return p1.order! < p2.order! ? -1 : 1;
          if (p1.active && !p2.active) return 1;
          if (!p1.active && p2.active) return -1;
          if (isBefore(trackerInfo1.tracker.lastUpdate, trackerInfo2.tracker.lastUpdate))
            return -1;
          else return 1;
        });
      });
      this.ensureActiveIsLast();
      this.loadingProjectList = false;
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  assignOrderToProjects() {
    this.trackers.forEach(t => {
      t.projects.forEach((p: Project, i: number) => {
        p.order = i;
      });
    });

    let tracker1LastProjectIndex = this.trackers[0].projects.length - 1;
    let tracker2LastProjectIndex = this.trackers[1].projects.length - 1;
    if (tracker1LastProjectIndex == tracker2LastProjectIndex) return;
    // Make sure both active projects have the same order index (otherwise wrong order of (ex-)active project in merged projects)
    if (this.trackers[0].projects[tracker1LastProjectIndex].order > this.trackers[1].projects[this.trackers[1].projects.length - 1].order)
      this.trackers[1].projects[tracker2LastProjectIndex].order = this.trackers[0].projects[tracker1LastProjectIndex].order
    else
      this.trackers[0].projects[tracker1LastProjectIndex].order = this.trackers[1].projects[tracker2LastProjectIndex].order;
  }

  sortProjects({ projects = [], byLastUpdate = false }: { projects: Project[], byLastUpdate: boolean }) {
    if (projects.length <= 1) return;
    projects.sort((p1, p2) => {
      if (!byLastUpdate || !p1.lastUpdate || !p2.lastUpdate) {
        return p1.order == p2.order ? 0 : p1.order! < p2.order! ? -1 : 1;
      }
      if (p1.frozen != p2.frozen)
        return p1.frozen ? -1 : 1;
      return isBefore(p1.lastUpdate, p2.lastUpdate) ? - 1 : 1;
    });
  }

  ensureActiveProjectIsLast(projects: Project[], project: Project) {
    let activeProjectIndex = projects?.length ? projects.findIndex(p => p.id.toString() == project?.id.toString()) : -1;
    if (activeProjectIndex >= 0 && activeProjectIndex != projects.length - 1) {
      projects.splice(activeProjectIndex, 1);
      projects.push({ ...project, active: true });
    } else if (activeProjectIndex == -1) {
      projects.push({ ...project, active: true });
    } else if (activeProjectIndex >= 0) {
      projects[activeProjectIndex]!.active = true;
    }

  }

  toggleTrackers() {
    this.mergeLeftToRight = !this.mergeLeftToRight;
    this.ensureActiveIsLast();
  }

  ensureActiveIsLast() {
    this.ensureActiveProjectIsLast(this.leftTracker.projects as Project[], this.leftTracker.project as Project);
    this.ensureActiveProjectIsLast(this.rightTracker.projects as Project[], this.rightTracker.project as Project);
    this.updateLeftRightProjects();
  }

  updateLeftRightProjects() {
    this.leftProjects = new Map();
    this.rightProjects = new Map();

    if (!this.leftTracker.sorted) {
      // sorting projects of trackers by order index, with active project last
      this.sortProjects({ projects: this.leftTracker.projects as Project[], byLastUpdate: false });
      this.sortProjects({ projects: this.rightTracker.projects as Project[], byLastUpdate: false });
      this.leftTracker.sorted = true;
    }
    // merging projects while making sure there are no duplicates
    this.mergedProjects = [...new Map([...this.leftTracker.projects!, ...this.rightTracker.projects as any[]].map(p => [p.id.toString(), p])).values()];
    // sorting merged projects by last update
    this.sortProjects({ projects: this.mergedProjects, byLastUpdate: true });
    if (this.mergeLeftToRight) {
      this.mergedTracker = { ...Object.assign({}, this.rightTracker) };
      // Filling leftProjects Map with leftTracker.projects
      this.leftTracker.projects!.forEach((project: any) => {
        project.active = false;
        if (project.id.toString() == (this.leftTracker.project as Project)!.id.toString())
          project.active = true;
        if (!this.leftProjects.has(project.id.toString()))
          this.leftProjects.set(project.id.toString(), { ...project });
      });
      // Filling rightProjects Map with merged projects (setting last project active)
      this.mergedProjects.forEach((project, i) => {
        project.active = false;
        if (i == this.mergedProjects.length - 1) {
          project.active = true;
          this.mergedTracker.project = { ...project };
        }
        if (!this.rightProjects.has(project.id.toString()))
          this.rightProjects.set(project.id.toString(), { ...project });
      });
      this.mergedTracker.projects = [...this.rightProjects.values()];
    } else {
      this.mergedTracker = { ...Object.assign({}, this.leftTracker) } as Tracker;
      // Filling rightProjects Map with rightTracker.projects
      this.rightTracker.projects.forEach((project: any) => {
        project.active = false;
        if (project.id == this.rightTracker.project)
          project.active = true;
        if (!this.rightProjects.has(project.id.toString()))
          this.rightProjects.set(project.id.toString(), { ...project });
      });

      // Filling leftProjects Map with merged projects (setting last project active)
      this.mergedProjects.forEach((project, i) => {
        project.active = false;
        if (i == this.mergedProjects.length - 1) {
          project.active = true;
          this.mergedTracker.project = { ...project };
        }
        if (!this.leftProjects.has(project.id.toString()))
          this.leftProjects.set(project.id.toString(), { ...project });
      });
      this.mergedTracker.projects = [...this.leftProjects.values()];
    }
    this.updateAllProjectsFrozenState();
  }

  updateAllProjectsFrozenState() {
    let projects = this.mergedTracker.projects as Project[];
    let nowFrozen = !!projects.length && projects.every(p => p.frozen);

    if (nowFrozen && !this.allProjectsFrozen)
      this.pop.warning(this.translate.instant("trackers.merge_frozen"));

    this.allProjectsFrozen = nowFrozen;
  }

  isLeftProject(project: Project) {
    let index = this.leftTracker.projects ? this.leftTracker.projects.findIndex((p: Project | string) => (typeof p == 'string' ? p : p.id).toString() == project.id.toString()) : -1;
    return index >= 0;
  }

  isRightProject(project: Project) {
    let index = this.rightTracker.projects ? this.rightTracker.projects.findIndex((p: Project | string) => (typeof p == 'string' ? p : p.id).toString() == project.id.toString()) : -1;
    return index >= 0;
  }

  getRightProjects(): any[] {
    return Array.from(this.rightProjects.values());
  }

  getLeftProjects(): any[] {
    // Convert Map values to an array and return
    return Array.from(this.leftProjects.values());
  }

  mergeTrackers() {
    if (this.allProjectsFrozen) return;

    let trackerToDelete: Tracker = (this.mergeLeftToRight ? this.leftTracker : this.rightTracker) as Tracker;
    this.trackerService.mergeTrackers({ toMerge: this.mergedTracker, toDelete: trackerToDelete })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.pop.toast(this.translate.instant("trackers.merge_success"), "success", 4000, "merge_type");
          this.dialogRef.close({ mergedTracker: this.mergedTracker, deletedTracker: trackerToDelete });
        },
        error: (err: any) => {
          console.error(err);
          this.pop.error(err.error.message);
        }
      })
  }
}
